<script lang="ts" setup>
import type {
  AvailableDeliveryDateType,
  AvailableDeliveryTimeType,
} from '~/api/order';
import OrderAPI from '~/api/order';
import type { UserAddressType } from '~/api/user';

const BLOCK_KEY = 'how-to-obtain-saving';
const TEST_ID = 'order-address-with-time-item';

const props = defineProps<{
  selected: boolean;
  address: UserAddressType;
}>();
const emit = defineEmits<{
  (event: 'select'): void;
}>();

const { t } = useI18n();
const orderStore = useOrderStore();
const modalStore = useModalStore();

const isDateTimeEdited = ref(false);
const selectedDate = ref<AvailableDeliveryDateType>();
const selectedTime = ref<AvailableDeliveryTimeType>();

const selectDeliveryRequest = reactive(
  useRequest(OrderAPI.selectDelivery, () => ({
    body: { type: 'courier', addressId: props.address.id },
  })),
);
const selectDeliveryDateTimeRequest = reactive(
  useRequest(OrderAPI.selectDeliveryDateTime, () => ({
    body: {
      date: selectedDate.value?.date || '',
      timeInterval: selectedTime.value?.value || '',
    },
  })),
);

const currentDelivery = computed(() => orderStore.delivery.current);
const dateOptions = computed(
  () =>
    (props.selected &&
      !selectDeliveryRequest.isLoading &&
      currentDelivery.value?.availableDates) ||
    null,
);
const selectedDateTime = computed(
  () =>
    (props.selected &&
      !selectDeliveryRequest.isLoading &&
      currentDelivery.value?.selectedDatetime) ||
    null,
);
const calculatedDeliveryDate = computed(
  () => currentDelivery.value?.calculatedDeliveryDate,
);
const state = computed(() => {
  if (selectDeliveryRequest.isLoading) return 'loading';
  if (props.selected && dateOptions.value) {
    return 'active';
  }
  if (props.selected && !selectedDateTime.value)
    return calculatedDeliveryDate.value ? 'calculated' : 'unknown';
  return 'none';
});

const onSubmitDateTime = () =>
  selectDeliveryDateTimeRequest
    .execute()
    ?.then((data) => {
      orderStore.deleteBlockKey(BLOCK_KEY);
      orderStore.updateOrderState(data);
      isDateTimeEdited.value = false;
    })
    .catch(() => {
      modalStore.showError({
        title: t('message.time_error'),
        action: t('message.action'),
      });
    });

watch(
  () => props.selected,
  (v) => {
    if (v) {
      selectDeliveryRequest
        .execute()
        ?.then(orderStore.updateOrderState)
        .catch((error) => {
          if (error.name === 'AbortError') return;

          modalStore.showError({
            title: t('message.delivery_error'),
            action: t('message.action'),
          });
        });
    }
  },
);

watch(
  () => currentDelivery.value?.addressId,
  () => {
    isDateTimeEdited.value = !!(
      currentDelivery.value &&
      currentDelivery.value.availableDates &&
      !currentDelivery.value.selectedDatetime
    );
    if (!isDateTimeEdited.value) {
      orderStore.deleteBlockKey(BLOCK_KEY);
    } else if (!selectedDateTime.value) {
      orderStore.setBlockKey(BLOCK_KEY);
    }
  },
  { immediate: true },
);

watch(
  dateOptions,
  (v) => {
    if (v && !v.some((el) => el.date === selectedDate.value?.date)) {
      selectedDate.value = v[0];
      selectedTime.value = selectedDate.value.timeIntervals[0];
      onSubmitDateTime();
    }
  },
  { immediate: true },
);

watch(selectedDate, (v) => {
  if (
    v &&
    !v.timeIntervals.some((el) => selectedTime.value?.value === el.value)
  )
    selectedTime.value = v.timeIntervals[0];
});
</script>

<template>
  <div class="addressWithTimeItem" :data-test-id="TEST_ID">
    <label
      class="addressWithTimeItem__label"
      :data-test-id="`${TEST_ID}-label`"
    >
      <span class="addressWithTimeItem__radioContainer">
        <BaseRadio
          @update:checked="emit('select')"
          :checked="selected"
          :value="address.id"
          dark
          size="s"
          name="courierObtainAddress"
          class="addressWithTimeItem__radio"
        />
        {{ userAddressToString(address) }}
      </span>
    </label>

    <BaseToggleTransition :state="state">
      <BaseLoader v-if="state === 'loading'" dark area />

      <div
        v-else-if="state === 'unknown'"
        class="addressWithTimeItem__footerContainer"
      >
        <span class="addressWithTimeItem__hint">
          {{ t('calculation_error') }}
        </span>
      </div>

      <div
        v-else-if="state === 'calculated' && calculatedDeliveryDate"
        class="addressWithTimeItem__footerContainer"
      >
        <span class="addressWithTimeItem__hint">
          {{ t('calculation_result', calculatedDeliveryDate) }}
        </span>
      </div>

      <template
        v-else-if="
          state === 'active' && dateOptions && selectedDate && selectedTime
        "
      >
        <div class="addressWithTimeItem__footerContainer">
          <span class="addressWithTimeItem__title">{{ t('select_date') }}</span>
          <BaseDayPicker
            v-model:value="selectedDate"
            @click="onSubmitDateTime"
            :date-options="dateOptions"
            class="addressWithTimeItem__dayPicker"
            :data-test-id="`${TEST_ID}-day-picker`"
          />
          <span class="addressWithTimeItem__title">
            {{ t('select_time') }}
          </span>
          <BaseTimeSlotPicker
            v-model:value="selectedTime"
            @click="onSubmitDateTime"
            :time-options="selectedDate.timeIntervals"
            :data-test-id="`${TEST_ID}-time-picker`"
          />
        </div>
      </template>
    </BaseToggleTransition>
  </div>
</template>

<i18n>
ru:
  calculation_result: Доставка может быть осуществлена в период с {dateFrom} по {dateTo}
  calculation_error: Извините, не удалось рассчитать время доставки
  select_date: Выберите день доставки
  select_time: Выберите время доставки
  save: Сохранить
  message:
    time_error: Во время выбора времени доставки произошла ошибка
    delivery_error: Во время выбора способа получения произошла ошибка
    action: Вернуться

en:
  calculation_result: Delivery can be made from {dateFrom} to {dateTo}
  calculation_error: Sorry, we couldn't calculate the delivery time
  select_date: Select delivery day
  select_time: Select delivery time
  save: Save
  message:
    time_error: An error occurred while selecting the delivery time
    delivery_error: An error occurred while selecting the delivery method
    action: Return
</i18n>

<style lang="scss">
.addressWithTimeItem {
  border-bottom: 1px solid #e6e6e6;

  &__label {
    display: block;
    padding: 16px 0 16px;
    font-size: 14px;
    line-height: 140%;
    letter-spacing: 0.2px;
    color: #121212;
    cursor: pointer;
  }

  &__radioContainer {
    display: flex;
    gap: 8px;
  }

  &__radio {
    padding: 2px;
  }

  &__selectedDateTime {
    margin-left: 28px;
    margin-top: 6px;
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 0;
    border: none;
    background: none;
    cursor: pointer;
    color: #121212;
    transition: color 0.3s ease;

    &:hover {
      color: #c0c0c0;
    }

    svg {
      display: block;
      width: 20px;
      height: 20px;
    }
  }

  &__title {
    display: block;
    font-size: 12px;
    line-height: 120%;
    letter-spacing: 0.2px;
    text-transform: uppercase;
    color: #121212;
    margin-bottom: 16px;
    margin-top: 16px;
  }

  &__hint {
    font-size: 14px;
    line-height: 140%;
    letter-spacing: 0.2px;
    color: #121212;
    display: block;
  }

  &__dayPicker {
    margin-bottom: 8px;
  }

  &__footerContainer {
    padding: 24px 0;
  }
}
</style>
